// Layout.tsx
import React, { ReactNode, useState, createContext, useContext, useCallback, useRef, useEffect } from 'react';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useOrderSocket } from '../../hooks/store/useOrderSocket';
import IncomingOrderAlertManager from '../store/orders/IncomingOrderAlertManager';

// Note: Layout is mounted for protected routes; we use the store socket here
// to keep the orderUpdated listener active across store pages.

const LayoutContext = createContext(false);

interface LayoutProps {
  children: ReactNode;
  title: string;
  headline: string;
}

const Layout: React.FC<LayoutProps> = ({ children, title, headline }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isNested = useContext(LayoutContext);
  const store = useSelector((state: any) => state.store?.store);
  const numericStoreId = store?.id;
  const role = useSelector((state: any) => state.auth?.role);
  const queryClient = useQueryClient();

  // OrderSocketManager handles a single socket connection, dedupe, and a sequential print queue.
  const OrderSocketManager: React.FC<{ storeId?: number; role?: string }> = ({ storeId, role }) => {
    type OrderUpdate = {
      orderId?: number;
      status?: string;
      customerId?: number;
      orderNumber?: string;
      autoPrintInvoice?: boolean;
    };

    const dedupeRef = useRef<Map<number, number>>(new Map()); // orderId -> expiryTimestamp
    const queueRef = useRef<number[]>([]);
    const queuedSetRef = useRef<Set<number>>(new Set());
    const processingRef = useRef(false);
    const mountedRef = useRef(true);

    useEffect(() => {
      mountedRef.current = true;
      return () => { mountedRef.current = false; };
    }, []);

    // Clean expired dedupe entries periodically to avoid growth when no events
    useEffect(() => {
      const interval = setInterval(() => {
        const now = Date.now();
        const map = dedupeRef.current;
        map.forEach((exp, id) => {
          if (exp <= now) map.delete(id);
        });
      }, 60000);
      return () => clearInterval(interval);
    }, []);

    const processQueue = useCallback(async () => {
      if (processingRef.current) return;
      processingRef.current = true;
      try {
        while (queueRef.current.length > 0 && mountedRef.current) {
          const orderId = queueRef.current.shift()!;
          queuedSetRef.current.delete(orderId);
          try {
            const { fetchInvoiceAndPrint } = await import('../../utils/printInvoice');
            await fetchInvoiceAndPrint(orderId, storeId, 'STORE');
          } catch (err) {
            console.error('Auto-print failed for order', orderId, err);
          }
        }
      } finally {
        processingRef.current = false;
      }
    }, [storeId]);

    const handleOrderUpdate = useCallback((data: OrderUpdate | undefined) => {
      try {
        // Re-broadcast for pages (keeps existing behavior)
        window.dispatchEvent(new CustomEvent('orderUpdated', { detail: data }));

        if (data?.status === 'incoming' || data?.status === 'pending_confirmation') {
          void queryClient.invalidateQueries({ queryKey: ['incoming-orders-count', storeId ?? 'all'] });
        }

        if (role !== 'STORE') return;
        if (!data || data.status !== 'preparing' || !data.orderId || data.autoPrintInvoice !== true) return;

        const now = Date.now();
        const map = dedupeRef.current;
        // cleanup expired entries inline
        map.forEach((exp, id) => {
          if (exp <= now) map.delete(id);
        });

        if (map.has(data.orderId)) {
          // duplicate within TTL, ignore
          return;
        }

        // mark as processed for TTL
        map.set(data.orderId, now + 30000); // 30s TTL

        // enqueue if not already queued
        if (!queuedSetRef.current.has(data.orderId)) {
          queueRef.current.push(data.orderId);
          queuedSetRef.current.add(data.orderId);
          // start processing (non-blocking)
          void processQueue();
        }
      } catch (err) {
        console.error('handleOrderUpdate error', err);
      }
    }, [role, processQueue, storeId]);

    useOrderSocket(storeId, handleOrderUpdate);

    return null;
  };

  // If already inside a Layout, just render children to avoid double sidebar/navbar
  if (isNested) {
    return <>{children}</>;
  }

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  return (
    <LayoutContext.Provider value={true}>
      <OrderSocketManager storeId={numericStoreId} role={role} />
      <IncomingOrderAlertManager />
      <div className="flex h-screen w-full overflow-hidden">
        {/* Sidebar */}
        <Sidebar
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
        />

        <div className="flex flex-1 min-w-0 flex-col overflow-hidden">
          <Navbar
            title={title}
            headline={headline}
            toggleSidebar={toggleSidebar}
          />

          <main className="flex-1 overflow-auto p-6">
            {children}
          </main>
        </div>
      </div>
    </LayoutContext.Provider>
  );
};

export default Layout;